import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  Spin,
  Row,
  Col,
  Divider,
  Tag,
  Descriptions,
  Image,
  Empty,
  Rate,
  Modal,
} from "antd";
import {
  ArrowLeftOutlined,
  CameraOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useParams } from "react-router-dom";
import { URL } from "../config";

export default function InstallationDetails() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [technicians, setTechnicians] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [inst, tech, cust, stat] = await Promise.all([
          axios.get(`${URL}/installations/${id}`),
          axios.get(`${URL}/technicians`),
          axios.get(`${URL}/customers`),
          axios.get(`${URL}/statuses`),
        ]);
        setData(inst.data.data || inst.data);
        setTechnicians(tech.data.data || tech.data || []);
        setCustomers(cust.data.data || cust.data || []);
        setStatuses(stat.data.data || stat.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [id]);

  const normalizeToArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        return [val];
      }
    }
    return [];
  };

  const getCustomerName = () => {
    if (!data || !customers.length) return "-";
    const customer = customers.find(
      (c) => String(c.CustomerId) === String(data.fkCustomerId)
    );
    return customer?.CustomerName || "-";
  };

  const getTechnicianName = () => {
    if (!data || !technicians.length) return "-";
    const tech = technicians.find(
      (t) => String(t.technicianId) === String(data.fktechnicianId)
    );
    return tech?.technicianName || "-";
  };

  const getStatusName = () => {
    if (!data || !statuses.length) return "-";
    const status = statuses.find(
      (s) => String(s.statusId) === String(data.fkstatusId)
    );
    return status?.statusName || "-";
  };

  const formatDateTime = (val) =>
    val
      ? new Date(val).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";

  const renderImages = (val) => {
    const images = normalizeToArray(val);
    if (!images.length) return <Empty description="No images" />;
    return (
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {images.map((img, i) => {
          const src = img.startsWith("http") ? img : `${URL}${img}`;
          return (
            <Image
              key={i}
              src={src}
              width={100}
              height={100}
              style={{
                objectFit: "cover",
                borderRadius: 12,
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
              onClick={() => {
                setPreviewImage(src);
                setPreviewVisible(true);
              }}
              preview={false}
            />
          );
        })}
      </div>
    );
  };

  if (loading)
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: 100 }}>
        <Spin size="large" />
      </div>
    );

  if (!data) return <Empty description="No data found" />;

  return (
    <div style={{ padding: 24, background: "#f4f6f8", minHeight: "100vh" }}>
      <Card
        style={{
          maxWidth: 1200,
          margin: "auto",
          borderRadius: 16,
          boxShadow: "0 8px 30px rgba(0,0,0,0.12), 0 0 12px rgba(0,0,0,0.08)",
        }}
      >
    
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            marginBottom: 24,
          }}
        >
          <h2 style={{ fontWeight: 700 }}>Installation Details</h2>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => window.history.back()}
            style={{ marginTop: 8 }}
          >
            Back
          </Button>
        </div>

     
        <Card
          title="Job Information"
          style={{
            marginBottom: 20,
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Installation ID">{data.InstallationId}</Descriptions.Item>
            <Descriptions.Item label="Request Title">{data.requestTitle}</Descriptions.Item>
            <Descriptions.Item label="Description">{data.Description}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={data.fkstatusId === 5 ? "green" : "blue"}>{getStatusName()}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Live Display">
              <Tag color={data.liveDisplay === 1 ? "green" : "red"}>
                {data.liveDisplay === 1 ? "Yes" : "No"}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Row gutter={[20, 20]}>
          <Col xs={24} sm={24} md={12}>
            <Card
              title="Customer Details"
              style={{
                borderRadius: 12,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Customer Name">{getCustomerName()}</Descriptions.Item>
                <Descriptions.Item label="Mobile">{data.MobileNo || "-"}</Descriptions.Item>
                <Descriptions.Item label="Address">{data.Address || "-"}</Descriptions.Item>
                <Descriptions.Item label="Customer Rating">
                  <Rate
                    disabled
                    allowHalf
                    value={data.customerRating || 0}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Customer Review">{data.customerReview || "-"}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          <Col xs={24} sm={24} md={12}>
            <Card
              title="Technician Details"
              style={{
                borderRadius: 12,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Technician Name">{getTechnicianName()}</Descriptions.Item>
                <Descriptions.Item label="Check-In">
                  <ClockCircleOutlined /> {formatDateTime(data.checkInTime)}
                </Descriptions.Item>
                <Descriptions.Item label="Check-Out">
                  <ClockCircleOutlined /> {formatDateTime(data.checkOutTime)}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>

  
        <Card
          title="Payment Details"
          style={{
            marginTop: 20,
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Payment Mode">{data.paymentMode || "-"}</Descriptions.Item>
            <Descriptions.Item label="Price">₹ {data.price || 0}</Descriptions.Item>
            <Descriptions.Item label="Discount">₹ {data.discount || 0}</Descriptions.Item>
            <Descriptions.Item label="Material Required">{data.materialRequired || "-"}</Descriptions.Item>
            <Descriptions.Item label="Material Used">{data.materialUsed || "-"}</Descriptions.Item>
          </Descriptions>
        </Card>

      
        <Divider />
        <h3>
          <CameraOutlined /> Installation Photos
        </h3>

        <Row gutter={[20, 20]}>
          <Col xs={24} sm={24} md={8}>
            <Card title="Technician Selfie">{renderImages(data.technicianSelfie)}</Card>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Card title="Before Work">{renderImages(data.beforePhoto)}</Card>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Card title="After Work">{renderImages(data.afterPhoto)}</Card>
          </Col>
        </Row>

        <Divider />
        <Card title="Other Photos">{renderImages(data.Photo)}</Card>

        <Modal
          visible={previewVisible}
          footer={null}
          onCancel={() => setPreviewVisible(false)}
        >
          <img
            alt="preview"
            style={{ width: "100%", borderRadius: 12 }}
            src={previewImage}
          />
        </Modal>
      </Card>
    </div>
  );
}
